// @givmo/app-charity — mailed-check election (CPA-WEB4).
//
// The alternative to ACH: elect to be paid by a mailed check. §3.4 rail: the mailing address is ALWAYS
// the READ-ONLY IRS address on file (GET .../payment-profile/check-address) — a user-typed address is
// never accepted. We show that address BEFORE the user elects, label its source honestly, and (if the
// IRS record is missing/unmailable) direct the org to update its IRS/GuideStar records instead of typing
// a new address here. Electing calls POST .../payment-profile/check (empty body) via the raw `sdk`.

import { useMutation, useQuery } from '@tanstack/react-query';
import { charityAdminGetCharityCheckAddressOptions } from '@givmo/api-client/query';
import {
  sdk,
  isGivmoError,
  type CheckMailingAddressOut,
  type CharityPaymentProfileResultOut,
} from '@givmo/api-client';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  ErrorMessage,
  Icon,
  Skeleton,
  testIdProp,
  toast,
} from '@givmo/design-system';
import { ArrowRight, Info, Mail, MapPin } from 'lucide-react';
import { useStepUpGate } from './use-step-up-gate';
import { isStepUpCancelled, unansweredRefusalLine } from './payment-step-up';

const PREFIX = 'payment-check';

/** Copy for an election the admin declined to verify — nothing changed, and it is theirs to retry. */
const STEP_UP_CANCELLED_MESSAGE =
  'Verification cancelled — nothing changed. Try again and verify it’s you to finish.';

export interface CheckElectionProps {
  charityId: string;
  /** Called with the MASKED result after a successful check election. */
  onElected: (result: CharityPaymentProfileResultOut) => void | Promise<unknown>;
  /** Optional cancel affordance (back out to method selection). */
  onCancel?: () => void;
}

/** Compose the IRS address into display lines; returns [] when there's nothing mailable to show. */
function addressLines(address: CheckMailingAddressOut): string[] {
  const lines: string[] = [];
  if (address.in_care_of) lines.push(`c/o ${address.in_care_of}`);
  if (address.street) lines.push(address.street);
  const cityLine = [address.city, address.state].filter(Boolean).join(', ');
  const cityZip = [cityLine, address.zip].filter(Boolean).join(' ').trim();
  if (cityZip) lines.push(cityZip);
  return lines;
}

export function CheckElection({ charityId, onElected, onCancel }: CheckElectionProps) {
  const addressQuery = useQuery(
    charityAdminGetCharityCheckAddressOptions({ path: { charity_id: charityId } }),
  );

  // AS-WEB3: `elect_check` is step-up gated like the ACH submit — a payout destination change either
  // way. The election carries no user-entered payload, so the retry is the identical empty POST.
  const stepUp = useStepUpGate();

  const elect = useMutation({
    mutationFn: (): Promise<CharityPaymentProfileResultOut> =>
      stepUp.run(async () => {
        const { data } = await sdk.charityAdminElectCharityCheck({
          path: { charity_id: charityId },
          body: {},
          throwOnError: true,
        });
        return data;
      }),
    onSuccess: async (result) => {
      await onElected(result);
    },
    onError: (error: unknown) => {
      if (isStepUpCancelled(error)) {
        toast(STEP_UP_CANCELLED_MESSAGE);
        return;
      }
      // A refusal nothing here could answer says its own cure (AS-PWL-2e), never "try again".
      toast.error(
        unansweredRefusalLine(error) ??
          (isGivmoError(error)
            ? error.message
            : 'We couldn’t set up mailed-check payments. Please try again.'),
      );
    },
  });

  const address = addressQuery.data;
  const lines = address ? addressLines(address) : [];
  const mailable = !!address && address.mailable !== false && lines.length > 0;

  return (
    <Card className="overflow-hidden" testId={`${PREFIX}-card`}>
      <CardHeader className="px-24 py-20">
        <div className="flex items-start gap-12">
          <span className="flex size-[40px] shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-600">
            <Icon icon={Mail} size={20} aria-hidden />
          </span>
          <div className="min-w-0">
            <p className="text-s11 font-w600 uppercase tracking-wide text-brand-700">
              Payment setup · Step 2 of 2
            </p>
            <h2 className="mt-2 text-s18 font-w700 text-text-strong">
              Confirm your mailing address
            </h2>
            <p className="mt-4 text-s13 text-text-secondary">
              Checks are mailed to your organization’s IRS address on file.
            </p>
          </div>
        </div>
      </CardHeader>
      <CardBody className="flex flex-col gap-20 px-24 py-24">
        {addressQuery.isPending ? (
          <div className="flex flex-col gap-8" {...testIdProp(`${PREFIX}-loading`)}>
            <Skeleton className="h-12 w-1/3" />
            <Skeleton className="h-12 w-2/3" />
            <Skeleton className="h-12 w-1/2" />
          </div>
        ) : addressQuery.isError ? (
          <EmptyState
            testId={`${PREFIX}-address-error`}
            icon={<Icon icon={MapPin} size={20} aria-hidden />}
            title="Couldn’t load your mailing address"
            description={
              isGivmoError(addressQuery.error)
                ? addressQuery.error.message
                : 'We couldn’t load your IRS address on file. Please try again.'
            }
            action={
              <Button
                appearance="saas"
                tone="secondary"
                size="sm"
                onClick={() => void addressQuery.refetch()}
                testId={`${PREFIX}-address-retry`}
              >
                Try again
              </Button>
            }
          />
        ) : mailable ? (
          <>
            <div
              className="rounded-xl border border-border bg-surface-sunken p-18"
              {...testIdProp(`${PREFIX}-address`)}
            >
              <div className="mb-14 flex items-start justify-between gap-12 border-b border-border-subtle pb-14">
                <div className="flex items-center gap-10">
                  <span className="flex size-32 items-center justify-center rounded-lg bg-surface text-brand-600 shadow-xs">
                    <Icon icon={MapPin} size={17} aria-hidden />
                  </span>
                  <div>
                    <h3 className="text-s14 font-w600 text-text-strong">Review mailing address</h3>
                    <p className="mt-1 text-s12 text-text-tertiary">
                      This is the address selected for mailed checks.
                    </p>
                  </div>
                </div>
                <Badge tone="neutral" size="sm">
                  IRS record
                </Badge>
              </div>
              <address className="pl-2 not-italic text-s14 font-w500 leading-relaxed text-text-strong">
                {lines.map((line) => (
                  <div key={line}>{line}</div>
                ))}
              </address>
            </div>
            <div
              className="flex items-start gap-8 rounded-lg border border-border-subtle bg-surface px-12 py-10"
              {...testIdProp(`${PREFIX}-address-source`)}
            >
              <Icon
                icon={Info}
                size={15}
                className="mt-1 shrink-0 text-text-tertiary"
                aria-hidden
              />
              <p className="text-s12 leading-h13 text-text-tertiary">
                This address comes from your IRS record and can’t be changed here. If it’s out of
                date, update your IRS/GuideStar records and it will refresh.
              </p>
            </div>
            {/* A failed election stays on the page. The toast above is the transient ack; a failure
                the operator can miss by looking away would leave an org believing its grants are
                routed when nothing was recorded. Mirrors `ach-form.tsx`'s submit-error rail. */}
            {elect.isError ? (
              <ErrorMessage testId={`${PREFIX}-submit-error`}>
                {isStepUpCancelled(elect.error)
                  ? STEP_UP_CANCELLED_MESSAGE
                  : (unansweredRefusalLine(elect.error) ??
                    'We couldn’t set up mailed-check payments. Please try again.')}
              </ErrorMessage>
            ) : null}

            <div className="flex flex-col-reverse items-stretch gap-8 border-t border-border-subtle pt-20 sm:flex-row sm:items-center sm:justify-end">
              {onCancel ? (
                <Button
                  appearance="saas"
                  tone="secondary"
                  size="lg"
                  onClick={onCancel}
                  disabled={elect.isPending}
                  testId={`${PREFIX}-cancel`}
                >
                  Cancel
                </Button>
              ) : null}
              <Button
                appearance="saas"
                tone="primary"
                size="lg"
                trailingIcon={<Icon icon={ArrowRight} size={16} aria-hidden />}
                loading={elect.isPending}
                onClick={() => elect.mutate()}
                testId={`${PREFIX}-elect`}
              >
                Receive grants by check
              </Button>
            </div>
          </>
        ) : (
          // No mailable IRS address → honest dead-end, NOT an election.
          <EmptyState
            testId={`${PREFIX}-no-address`}
            icon={<Icon icon={MapPin} size={20} aria-hidden />}
            title="No mailing address on file"
            description="We don’t have a mailable IRS address for your organization yet, so we can’t set up mailed checks. Update your IRS/GuideStar records, or add a bank account for direct deposit instead."
            action={
              onCancel ? (
                <Button
                  appearance="saas"
                  tone="secondary"
                  size="sm"
                  onClick={onCancel}
                  testId={`${PREFIX}-no-address-back`}
                >
                  Back
                </Button>
              ) : null
            }
          />
        )}
      </CardBody>
      {/* Portalled — see the ACH form: rendered here so the prompt is owned by the panel it guards. */}
      {stepUp.dialog}
    </Card>
  );
}
